import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import authService from '../services/authService'
import { useTranslation } from '../hooks/use-i18n'

interface ForgotPasswordForm {
  email: string
}

const ForgotPassword: React.FC = () => {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [resetLink, setResetLink] = useState<string | null>(null)
  const { register, handleSubmit, formState: { errors } } = useForm<ForgotPasswordForm>()

  const onSubmit = async (data: ForgotPasswordForm) => {
    try {
      setIsLoading(true)
      setResetLink(null)
      const result = await authService.forgotPassword(data.email.trim().toLowerCase())
      setSubmitted(true)
      if (result.resetLink) setResetLink(result.resetLink)
      toast.success(
        result.resetLink
          ? t('auth.resetLinkDevToast')
          : t('auth.resetLinkToast')
      )
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('auth.forgotPasswordError'))
    } finally {
      setIsLoading(false)
    }
  }

  const copyLink = () => {
    if (resetLink) {
      navigator.clipboard.writeText(resetLink)
      toast.success(t('auth.linkCopiedToast'))
    }
  }

  return (
    <div className="min-h-screen gradient-bg flex items-center justify-center pt-12 sm:pt-16 lg:pt-20 pb-6 sm:pb-12 px-3 xs:px-4 overflow-x-hidden">
      <div className="max-w-md w-full min-w-0">
        <div className="glass-strong rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 xl:p-10">
          <div className="text-center mb-4 sm:mb-6 lg:mb-8">
            <h1 className="font-display text-lg sm:text-xl lg:text-display-2 font-bold mb-1.5 sm:mb-2 text-foreground">{t('auth.forgotPasswordTitle')}</h1>
            <p className="text-small sm:text-body-lg text-foreground/90">{t('auth.forgotPasswordDesc')}</p>
          </div>

          {submitted ? (
            <div className="space-y-4 sm:space-y-6">
              <p className="text-foreground/90 text-center text-small sm:text-body-lg">
                {resetLink
                  ? t('auth.resetLinkDev')
                  : t('auth.resetLinkSent')}
              </p>
              {resetLink && (
                <div className="space-y-2 sm:space-y-3">
                  <a href={resetLink} className="block text-small sm:text-nav text-primary hover:underline break-all">
                    {resetLink}
                  </a>
                  <button onClick={copyLink} className="w-full py-2 sm:py-2.5 px-3 sm:px-4 rounded-lg sm:rounded-xl bg-muted/20 border border-border hover:bg-muted/30 text-foreground font-medium text-small sm:text-nav transition-all">
                    {t('auth.copyLink')}
                  </button>
                </div>
              )}
              <button onClick={() => { setSubmitted(false); setResetLink(null) }} className="w-full py-2.5 sm:py-3 px-3 sm:px-4 rounded-lg sm:rounded-xl bg-muted/20 border border-border hover:bg-muted/30 text-foreground font-semibold text-small sm:text-nav transition-all">
                {t('auth.sendAnotherLink')}
              </button>
            </div>
          ) : (
            <form className="space-y-3 sm:space-y-5" onSubmit={handleSubmit(onSubmit)}>
              <div>
                <label htmlFor="email" className="text-small sm:text-nav font-medium mb-1.5 sm:mb-2 block text-foreground">{t('auth.email')}</label>
                <input
                  {...register('email', { required: t('auth.emailRequired'), pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: t('auth.emailInvalid') } })}
                  type="email"
                  className="w-full min-w-0 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl bg-muted/20 border border-border text-foreground placeholder:text-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all text-small sm:text-body"
                  placeholder={t('auth.enterEmail')}
                />
                {errors.email && <p className="mt-1.5 text-small text-destructive">{errors.email.message}</p>}
              </div>
              <button type="submit" disabled={isLoading} className="w-full py-2.5 sm:py-3 lg:py-3.5 rounded-lg sm:rounded-xl bg-primary text-primary-foreground font-semibold text-small sm:text-nav hover:opacity-90 transition-all glow-subtle disabled:opacity-50 disabled:cursor-not-allowed">
                {isLoading ? t('auth.sending') : t('auth.sendResetLink')}
              </button>
            </form>
          )}

          <div className="mt-4 sm:mt-6 text-center">
            <Link to="/login" className="text-small sm:text-nav text-foreground/85 hover:text-foreground transition-colors">
              {t('auth.backToSignIn')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ForgotPassword
